import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { ProviderTier } from '../types/providers';
import type { Job } from '../types/operations';

interface ApiKeys {
  replicate?: string;
  huggingface?: string;
  fal?: string;
}

interface ApiContextValue {
  tier: ProviderTier;
  setTier(t: ProviderTier): void;
  apiKeys: ApiKeys;
  setApiKey(provider: keyof ApiKeys, key: string): void;
  jobs: Job[];
  addJob(job: Job): void;
  updateJob(id: string, updates: Partial<Job>): void;
}

const ApiContext = createContext<ApiContextValue | null>(null);

export function useApi(): ApiContextValue {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error('useApi must be used within ApiProvider');
  return ctx;
}

const STORAGE_KEY = 'meda-api-keys';
const TIER_KEY = 'meda-tier';

function loadKeys(): ApiKeys {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function loadTier(): ProviderTier {
  return (localStorage.getItem(TIER_KEY) as ProviderTier) || 'free';
}

export function ApiProvider({ children }: { children: ReactNode }) {
  const [apiKeys, setApiKeys] = useState<ApiKeys>(loadKeys);
  const [tier, setTierState] = useState<ProviderTier>(loadTier);
  const [jobs, setJobs] = useState<Job[]>([]);

  const setApiKey = useCallback((provider: keyof ApiKeys, key: string) => {
    setApiKeys(prev => {
      const next = { ...prev, [provider]: key };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const setTier = useCallback((t: ProviderTier) => {
    setTierState(t);
    localStorage.setItem(TIER_KEY, t);
  }, []);

  const addJob = useCallback((job: Job) => {
    setJobs(prev => [job, ...prev]);
  }, []);

  const updateJob = useCallback((id: string, updates: Partial<Job>) => {
    setJobs(prev => prev.map(j => j.id === id ? { ...j, ...updates } : j));
  }, []);

  return (
    <ApiContext.Provider value={{ tier, setTier, apiKeys, setApiKey, jobs, addJob, updateJob }}>
      {children}
    </ApiContext.Provider>
  );
}
